import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiClient from '../api/api';
import { Map, MapMarker, MapRoute, MapControls, MarkerContent, MarkerLabel } from '../components/ui/map';
import { FaArrowLeft, FaMapMarkerAlt, FaLocationArrow, FaCheckCircle, FaClock } from 'react-icons/fa';
import { Loader2 } from 'lucide-react';

const WorkerJobMap = () => {
    const { hireId } = useParams();
    const navigate = useNavigate();
    const [hire, setHire] = useState(null);
    const [jobLocation, setJobLocation] = useState(null); // Destination
    const [workerPos, setWorkerPos] = useState(null); // Source
    const [routeCoords, setRouteCoords] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    const watchId = useRef(null);
    const lastUpdate = useRef(0);

    // 1. Fetch Hire & Job Data
    useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);
                // We need hire details + job location
                // The /hires/my-hires returns everything, but let's fetch specific if we had an endpoint.
                // For now, let's find it from the list or create a get-by-id
                const res = await apiClient.get('/hires/my-hires');
                // Ensure ID comparison is robust (string vs number if applicable, though UUIDs are strings)
                const currentHire = res.data.find(h => String(h.id) === String(hireId));
                
                if (!currentHire) {
                    console.error('Hire not found in list. HireId:', hireId, 'List:', res.data.map(h => h.id));
                    setError('Hire record not found.');
                    setLoading(false);
                    return;
                }

                setHire(currentHire);

                // Determine Destination Location
                let dest = null;
                
                // Helper to parse location
                const parseLoc = (loc) => {
                    if (!loc) return null;
                    if (typeof loc === 'object' && loc.lat) return { lat: loc.lat, lng: loc.lng, address: loc.address || loc.granular_location };
                    if (typeof loc === 'string') {
                        try {
                            const parsed = JSON.parse(loc);
                            if (parsed.lat) return { lat: parsed.lat, lng: parsed.lng, address: parsed.address };
                        } catch (e) {
                            // If string but not JSON, we can't map it without geocoding. 
                            // For now, assume it's invalid for map unless we geocode it here.
                            return null;
                        }
                    }
                    return null;
                };

                if (currentHire.job_post_id) {
                    // Check if job_post is already nested (from Direct_Hires include)
                    // The standardized response from /my-hires might not deeply nest everything or might strip it.
                    // Let's fetch clean to be sure, or check if present.
                    // If fetching:
                    try {
                        const jobRes = await apiClient.get(`/jobs/${currentHire.job_post_id}`);
                        dest = parseLoc(jobRes.data.location);
                    } catch (e) {
                        console.error("Failed to fetch job details", e);
                    }
                } 
                
                if (!dest && currentHire.buyer_id) {
                    // Fallback to buyer profile location
                    try {
                        const profileRes = await apiClient.get(`/profiles/${currentHire.buyer_id}`);
                        dest = parseLoc(profileRes.data.location);
                    } catch (e) {
                        console.error("Failed to fetch buyer profile", e);
                    }
                }

                if (!dest) {
                    setError('Destination location not set for this job.');
                } else {
                    setJobLocation(dest);
                }

            } catch (err) {
                console.error(err);
                setError('Failed to load tracking data.');
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [hireId]);

    // 2. Track Live Location & Update Backend
    const updateBackendLocation = useCallback(async (lat, lng) => {
        const now = Date.now();
        // Throttle updates to every 30 seconds to save battery/data
        if (now - lastUpdate.current < 30000) return;
        
        try {
            await apiClient.put(`/hires/${hireId}/location`, { lat, lng });
            lastUpdate.current = now;
            console.log('Backend location updated:', lat, lng);
        } catch (err) {
            console.error('Failed to update live location on server:', err);
        }
    }, [hireId]);

    useEffect(() => {
        if (!("geolocation" in navigator)) {
            setError("Geolocation is not supported by your browser.");
            return;
        }

        watchId.current = navigator.geolocation.watchPosition(
            (pos) => {
                const { latitude, longitude } = pos.coords;
                setWorkerPos({ lat: latitude, lng: longitude });
                updateBackendLocation(latitude, longitude);
            },
            (err) => {
                console.error(err);
                // Don't set blocking error, maybe worker is just in a tunnel
            },
            { enableHighAccuracy: true, maximumAge: 10000, timeout: 5000 }
        );

        return () => {
            if (watchId.current) navigator.geolocation.clearWatch(watchId.current);
        };
    }, [updateBackendLocation]);

    // 3. Fetch OSRM Route
    useEffect(() => {
        if (workerPos && jobLocation) {
            const fetchRoute = async () => {
                try {
                    const response = await fetch(
                        `https://router.project-osrm.org/route/v1/driving/${workerPos.lng},${workerPos.lat};${jobLocation.lng},${jobLocation.lat}?overview=full&geometries=geojson`
                    );
                    const data = await response.json();
                    if (data.routes && data.routes.length > 0) {
                        setRouteCoords(data.routes[0].geometry.coordinates);
                    }
                } catch (err) {
                    console.error('Routing failed:', err);
                }
            };
            fetchRoute();
        }
    }, [workerPos, jobLocation]);

    if (loading) return (
        <div className="flex flex-col items-center justify-center h-screen bg-slate-50">
            <Loader2 className="size-10 animate-spin text-emerald-600 mb-4" />
            <p className="font-bold text-slate-600 tracking-tight">Initializing Navigation...</p>
        </div>
    );

    if (error) return (
        <div className="flex flex-col items-center justify-center h-screen bg-slate-50 p-6">
            <div className="bg-white p-8 rounded-[2rem] shadow-xl border border-red-100 text-center max-w-md">
                <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center mx-auto mb-4">
                    <FaMapMarkerAlt size={30} />
                </div>
                <h2 className="text-xl font-black text-slate-800 mb-2">Navigation Error</h2>
                <p className="text-slate-500 mb-6">{error}</p>
                <button onClick={() => navigate(-1)} className="w-full py-3 bg-slate-900 text-white rounded-xl font-bold">Go Back</button>
            </div>
        </div>
    );

    return (
        <div className="h-screen flex flex-col font-sans overflow-hidden">
            {/* Overlay Header */}
            <div className="absolute top-4 left-4 right-4 z-10 flex items-center gap-3">
                <button onClick={() => navigate(-1)} className="p-4 bg-white/90 backdrop-blur shadow-lg rounded-2xl text-slate-800 hover:bg-slate-50 transition-all">
                    <FaArrowLeft />
                </button>
                <div className="flex-1 bg-white/90 backdrop-blur shadow-lg rounded-2xl p-4 border border-emerald-100">
                    <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">Destination</p>
                    <p className="text-sm font-bold text-slate-800 line-clamp-1">{jobLocation?.address || 'Job Site'}</p>
                </div>
            </div>

            {/* Map Area */}
            <div className="flex-1 relative bg-slate-200">
                <Map 
                    center={workerPos ? [workerPos.lng, workerPos.lat] : [90.4125, 23.8103]} 
                    zoom={15} 
                    className="h-full w-full"
                >
                    <MapControls showLocate showZoom showCompass position="bottom-right" />
                    
                    {/* Worker Pin */}
                    {workerPos && (
                        <MapMarker longitude={workerPos.lng} latitude={workerPos.lat}>
                            <MarkerContent>
                                <div className="size-8 bg-emerald-600 rounded-full border-4 border-white shadow-xl flex items-center justify-center text-white ring-4 ring-emerald-500/20">
                                    <FaLocationArrow className="rotate-45" size={12} />
                                </div>
                                <MarkerLabel position="bottom">You</MarkerLabel>
                            </MarkerContent>
                        </MapMarker>
                    )}

                    {/* Job Pin */}
                    {jobLocation && (
                        <MapMarker longitude={jobLocation.lng} latitude={jobLocation.lat}>
                            <MarkerContent>
                                <div className="size-10 bg-slate-900 rounded-2xl border-4 border-white shadow-xl flex items-center justify-center text-white">
                                    <FaCheckCircle size={16} className="text-emerald-400" />
                                </div>
                                <MarkerLabel position="top">Job Site</MarkerLabel>
                            </MarkerContent>
                        </MapMarker>
                    )}

                    {/* Route Line */}
                    {routeCoords.length > 0 && (
                        <MapRoute 
                            coordinates={routeCoords} 
                            color="#059669" 
                            width={6} 
                            opacity={0.8} 
                        />
                    )}
                </Map>
            </div>

            {/* Bottom Info Sheet */}
            <div className="bg-white p-6 rounded-t-[2.5rem] shadow-[0_-10px_40px_rgba(0,0,0,0.1)] border-t border-slate-100 relative z-10">
                <div className="w-12 h-1.5 bg-slate-200 rounded-full mx-auto mb-6"></div>
                <div className="flex items-center justify-between gap-6">
                    <div className="flex-1">
                        <h3 className="text-xl font-black text-slate-800 leading-tight mb-1">{hire?.description || 'Active Contract'}</h3>
                        <p className="text-sm font-bold text-slate-400 flex items-center gap-2">
                            <FaClock /> Tracking Active
                        </p>
                    </div>
                    <div className="text-right">
                        <p className="text-2xl font-black text-emerald-600">৳{hire?.total_amount}</p>
                        <p className="text-[10px] font-black text-slate-400 uppercase tracking-wider">Contract Budget</p>
                    </div>
                </div>
                
                <div className="mt-6 grid grid-cols-2 gap-3">
                    <button className="py-4 bg-slate-100 text-slate-600 rounded-2xl font-black text-sm hover:bg-slate-200 transition-all">
                        Support
                    </button>
                    <button onClick={() => navigate(-1)} className="py-4 bg-emerald-600 text-white rounded-2xl font-black text-sm shadow-lg shadow-emerald-200 hover:bg-emerald-700 transition-all">
                        I've Arrived
                    </button>
                </div>
            </div>
        </div>
    );
};

export default WorkerJobMap;
